/*
 * @providesModule Sizes
 *
 */

// TODO Imports changed in order for specs to run

import _ from 'lodash';
import {Dimensions, NativeModules, Platform} from 'react-native';

import SKZConstants from '../Constants/_SKZConstants';

const SKZAPIManager = NativeModules.SKZAPIManager;
const SkillzModule = NativeModules.SkillzModule;

const IPHONE_4_HEIGHT = 480;
const IPHONE_5_HEIGHT = 568;
const IPHONE_6_HEIGHT = 667;
const IPHONE_6_PLUS_HEIGHT = 736;
export const IPHONE_X_HEIGHT = 812;
const IPHONE_XS_MAX_HEIGHT = 896;
const IPHONE_12_HEIGHT = 844;
const IPHONE_12_MAX_HEIGHT = 926;
const IPHONE_14_MAX_HEIGHT = 932;
const GOOGLE_PIXEL_XL_WIDTH = 486;

const IPAD_HEIGHT = 768;

const TABBAR_HEIGHT = 60;
const USER_INFO_HEIGHT = 96;
// const STATUS_BAR_HEIGHT = 20; status bar is not shown anymore
const SMALL_TABLET_MIN_DIM_THRESHOLD = 852;
const SMALL_ANDROID_TABLET_MIN_DIM_THRESHOLD = 752;

const MENU_BAR_WIDTH_PERCENTAGE = 0.28;

enum IPhoneClass {
  IPHONE_UNKNOWN_CLASS = 0,
  IPHONE_FOUR_CLASS = 1,
  IPHONE_FIVE_CLASS = 2,
  IPHONE_SIX_CLASS = 3,
  IPHONE_SIX_PLUS_CLASS = 4,
  IPAD_CLASS = 5,
}

interface ScreenOrientationSelector {
  portrait: any;
  landscape: any;
}
interface GameOrientationSelector {
  gameLandscape: any;
  gamePortrait: any;
}

export class SizesClass {
  private readonly resizeListener: (() => void) | null = null;
  private _webScreenWidth: number = typeof window !== 'undefined' ? window.innerWidth : Dimensions.get('window').width;
  private _webScreenHeight: number = typeof window !== 'undefined' ? window.innerHeight : Dimensions.get('window').height;
  private forcePortrait: boolean = false;

  constructor(forcePortrait: boolean = false) {
    if (Platform.OS === 'web') {
      this.resizeListener = this.updateDimensions.bind(this);
    }

    this.forcePortrait = forcePortrait;
  }

  dynamic(map: {
    sm?: any;
    md?: any;
    lg?: any;
    xlg?: any;
    xxlg?: any;
    phone?: any;
    tablet?: any;
  }): any {
    const maxAxis = Math.max(this.screenHeight, this.screenWidth);
    const minAxis = Math.min(this.screenHeight, this.screenWidth);

    const small = map.sm || map.phone || 0;
    const medium = map.md != undefined ? map.md : small;
    const large = map.lg != undefined ? map.lg : medium;
    const xlarge = map.xlg != undefined ? map.xlg : large;
    let xxlarge;
    if (map.xxlg != undefined) {
      xxlarge = map.xxlg;
    } else if (map.tablet != undefined) {
      xxlarge = map.tablet;
    } else {
      xxlarge = large;
    }

    if (maxAxis <= IPHONE_6_HEIGHT) {
      // Height <= 667 -> iPhone 8 or smaller
      return small;
    } else if (maxAxis <= IPHONE_X_HEIGHT) {
      // Height <= 812 -> iPhone X or iPhone 13 mini or smaller
      return medium;
    } else if (maxAxis < IPHONE_14_MAX_HEIGHT) {
      // Height < 932 -> iPhone 12 Pro or smaller
      return large;
    } else if (!this.isTablet()) {
      // Android phablets or any iPhone Pro Max or larger
      return xlarge;
    }
    // iPad
    return xxlarge;
  }

  /**
   * Initializes resize listening for web and sets initial dimensions.
   */
  startListening() {
    if (Platform.OS === 'web' && this.resizeListener) {
      // Resize event listener
      window.addEventListener('resize', this.resizeListener);

      // Orientation change listener
      const mediaQuery = window.matchMedia('(orientation: portrait)');
      mediaQuery.addEventListener('change', this.resizeListener);

      this.updateDimensions(); // Set initial dimensions
    }
  }

  /**
   * Removes resize listening to prevent memory leaks.
   */
  stopListening() {
    if (Platform.OS === 'web' && this.resizeListener) {
      // Remove resize listener
      window.removeEventListener('resize', this.resizeListener);

      // Remove orientation change listener
      const mediaQuery = window.matchMedia('(orientation: portrait)');
      mediaQuery.removeEventListener('change', this.resizeListener);
    }
  }

  /**
   * Updates screen dimensions and orientation when the window is resized.
   */
  private updateDimensions() {
    this._webScreenWidth = window.innerWidth;
    this._webScreenHeight = window.innerHeight;
  }

  _deviceClassification: IPhoneClass = IPhoneClass.IPHONE_UNKNOWN_CLASS;

  get deviceClassification(): IPhoneClass {
    if (!this._deviceClassification) {
      const maxDimension = Math.max(this.screenHeight, this.screenWidth);

      if (maxDimension < IPHONE_6_HEIGHT) {
        this._deviceClassification = IPhoneClass.IPHONE_FOUR_CLASS;
        if (maxDimension >= IPHONE_5_HEIGHT) {
          this._deviceClassification = IPhoneClass.IPHONE_FIVE_CLASS;
        }
      } else {
        this._deviceClassification = IPhoneClass.IPHONE_SIX_CLASS;
        if (maxDimension >= IPHONE_6_PLUS_HEIGHT) {
          this._deviceClassification = IPhoneClass.IPHONE_SIX_PLUS_CLASS;
          if (maxDimension >= IPAD_HEIGHT) {
            this._deviceClassification = IPhoneClass.IPAD_CLASS;
          }
        }
      }
    }
    return this._deviceClassification;
  }

  /**
   *
   *   keys are of the form [Size]?[Device]?[Orientation]? | 'other'
   *   where:
   *         Orientation = Portrait | Landscape
   *         Device = Phone | Minitab | Tablet
   *         Size = Small | Medium | Large | Huge
   *
   *  Keys components are case-insenstive and order agnostic - so landscapePhone and Phone_landscape are equivalent
   *  Size matches if it is the same or smaller than the current device's screen, with larger matching sizes preferred
   *  The 'other" key is selected if none of the other provided keys match - all calls to select() must provide at
   *  least one matching key or provide a default value
   *
   *  when converting from dynamic() use 'huge' for 'xlg' and 'tablet' for 'xxlg'
   *
   *  Key components are strictly hierarchical with Orientation > Device > Size
   *  So a matching key with a "Landscape" component will trump all other matching keys without a "Landscape" component
   *  e.g. an iPhone 4 in landscape orientation would select "landscape" over "smallPhone"'
   *
   * Minitab refers to the small android tablets, as determined by Sizes._isAndroidSmallTablet
   * These small tablets will match the Minitab device key if provided, otherwise they will match Tablet
   */

  select(
    map: Record<string, any>,
    defaultValue: any = null,
    testArgs: {
      isTablet?: boolean;
      isMinitab?: boolean;
      isLandscape?: boolean;
      isBrowser?: boolean;
      isGameLandscape?: boolean;
      phoneClass?: IPhoneClass;
    } | null = null
  ): any {
    const isTablet = testArgs !== null ? testArgs.isTablet : Sizes.isTablet();
    const isMinitab = testArgs !== null ? testArgs.isMinitab : Sizes._isAndroidSmallTablet();
    const isLandscape = testArgs !== null ? testArgs.isLandscape : Sizes.isLandscape();
    const isGameLandscape = testArgs !== null ? testArgs.isGameLandscape : Sizes.isGameLandscape();
    const isBrowser = testArgs !== null ? testArgs.isBrowser : Sizes.isBrowser();
    const phoneClass =
      testArgs !== null
        ? testArgs.phoneClass || IPhoneClass.IPHONE_UNKNOWN_CLASS
        : Sizes.deviceClassification;
    const kSizeClasses = ['small', 'medium', 'large', 'huge'];
    const bigBonus = 16;
    const smallBonus = 8;
    const LANDSCAPE = 'landscape';
    const PORTRAIT = 'portrait';
    const GAME_LANDSCAPE = 'gamelandscape';
    const GAME_PORTRAIT = 'gameportrait';
    const TABLET = 'tablet';
    const MINITAB = 'minitab';
    const PHONE = 'phone';
    const OTHER = 'other';
    const BROWSER = 'browser';

    let bestScore = 0;
    let result = defaultValue;
    let score = 0;

    for (const originalKey in map) {
      if (!map.hasOwnProperty(originalKey)) {
        continue;
      }
      let key = originalKey.toLowerCase();
      score = 0;

      if (key.includes(GAME_LANDSCAPE)) {
        key = key.replace(GAME_LANDSCAPE, '');
        if (!isGameLandscape) {
          continue;
        }
        score += bigBonus;
      } else if (key.includes(GAME_PORTRAIT)) {
        key = key.replace(GAME_PORTRAIT, '');
        if (isGameLandscape) {
          continue;
        }
        score += bigBonus;
      } else if (key.includes(LANDSCAPE)) {
        key = key.replace(LANDSCAPE, '');
        if (!isLandscape) {
          continue;
        }
        score += bigBonus;
      } else if (key.includes(PORTRAIT)) {
        key = key.replace(PORTRAIT, '');
        if (isLandscape) {
          continue;
        }
        score += bigBonus;
      }

      if (bestScore >= bigBonus && score < bigBonus) {
        continue;
      }

      if (key.includes(MINITAB)) {
        key = key.replace(MINITAB, '');
        if (!isMinitab) {
          continue;
        }
        score += smallBonus + 1;
      } else if (key.includes(TABLET)) {
        key = key.replace(TABLET, '');
        if (!isTablet) {
          continue;
        }
        score += smallBonus;
      } else if (key.includes(PHONE)) {
        key = key.replace(PHONE, '');
        if (isTablet) {
          continue;
        }
        score += smallBonus;
      }
      if (key === BROWSER && isBrowser) {
        score = 1;
        key = '';
      }
      if (key === OTHER) {
        score = 1;
        key = '';
      }

      if (key.length) {
        let bonus = 2;
        // consume all the keys to avoid unrecognized selector warning below
        for (let index = 0; index < kSizeClasses.length; index++) {
          const sizeClass = kSizeClasses[index];
          if (sizeClass && key.includes(sizeClass)) {
            key = key.replace(sizeClass, '');
            if (index < phoneClass) {
              score += bonus;
            }
          }
          bonus++;
        }
      }
      if (key.length > 0) {
        console.warn(`unrecognized selector ${key} (${originalKey}) passed to Sizes.select()`);
      }
      if (score > bestScore) {
        result = map[originalKey];
        bestScore = score;
      }
    }
    if (bestScore === 0) {
      if (defaultValue === null) {
        console.warn('Sizes.select() called without a matching key');
        // Return first value in map so UI never gets undefined and breaks render (avoids blank lobby)
        const firstKey = Object.keys(map)[0];
        if (firstKey !== undefined) {
          result = map[firstKey];
        }
      }
    }
    return result;
  }

  orientation(map: GameOrientationSelector | ScreenOrientationSelector): any {
    if (_.has(map, 'gameLandscape')) {
      const gameOrientationSelector = map as GameOrientationSelector;

      if (this.isGameLandscape()) {
        return gameOrientationSelector.gameLandscape;
      }
      return gameOrientationSelector.gamePortrait;
    }
    const screenOrientation = map as ScreenOrientationSelector;

    if (this.isLandscape()) {
      return screenOrientation.landscape;
    }
    return screenOrientation.portrait;
  }

  gameOrientation(): string {
    return (
      (SkillzModule.App.IsGameLandscape && SKZConstants.SKZ_LANDSCAPE) || SKZConstants.SKZ_PORTRAIT
    );
  }

  systemVersionIOS(map: Record<string, any>): string {
    if (map && Object.keys(map)) {
      let matchingValue = '';
      const currentVersion = SKZAPIManager.OSVersion;
      const mapKeys = Object.keys(map);

      if (Array.isArray(mapKeys)) {
        mapKeys.forEach((key) => {
          if (currentVersion >= key) {
            matchingValue = map[key];
          }
        });
      }
      return matchingValue;
    }
    return '';
  }

  get screenWidth(): number {
    return Platform.OS === 'web' ? this._webScreenWidth : Dimensions.get('screen').width;
  }

  get screenHeight(): number {
    return Platform.OS === 'web' ? this._webScreenHeight : Dimensions.get('screen').height;
  }

  get landscapeScreenWidth(): number {
    return Dimensions.get('screen').height;
  }

  get landscapeScreenHeight(): number {
    return Dimensions.get('screen').width;
  }

  get tabViewWidth(): number {
    if (this.isPortrait() || this.isTablet()) {
      return this.screenWidth;
    }
    return this.screenWidth * (1.0 - MENU_BAR_WIDTH_PERCENTAGE);
  }

  get tabViewHeight(): number {
    if (this.isPortrait() || this.isTablet()) {
      return this.screenHeight - TABBAR_HEIGHT - USER_INFO_HEIGHT;
    }
    return this.screenHeight;
  }

  getUIWidth(): number {
    return this.screenWidth;
  }

  getUIHeight(): number {
    return this.screenHeight;
  }

  isBrowser(): boolean {
    return Platform.OS === 'web';
  }

  isTablet(): boolean {
    const maxAxis = Math.max(this.screenWidth, this.screenHeight);
    const minAxis = Math.min(this.screenWidth, this.screenHeight);
    return maxAxis > IPHONE_6_PLUS_HEIGHT && minAxis > GOOGLE_PIXEL_XL_WIDTH;
  }

  isPhone(): boolean {
    return !this.isTablet();
  }

  isPlusSizedPhone(): boolean {
    return !this.isTablet() && Math.max(this.screenWidth, this.screenHeight) > IPHONE_6_HEIGHT;
  }

  isSmallPhone(): boolean {
    return !this.isTablet() && Math.max(this.screenWidth, this.screenHeight) < IPHONE_6_HEIGHT;
  }

  isPortrait(): boolean {
    if (this.forcePortrait) {
      return true;
    }

    if (Platform.OS === 'web') {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      return screenWidth < screenHeight;
    }
    const screenWidth = Dimensions.get('window').width;
    const screenHeight = Dimensions.get('window').height;
    return screenWidth < screenHeight;
  }

  isGamePortrait(): boolean {
    return !this.isGameLandscape();
  }

  isLandscape(): boolean {
    if (this.forcePortrait) {
      return false;
    }

    return !this.isPortrait();
  }

  isGameLandscape(): boolean {
    return SkillzModule.App.IsLandscape;
  }

  isLandscapePhone(): boolean {
    return this.isLandscape() && this.isPhone();
  }

  isNotchedIphone(): boolean {
    const screenMeasurement = this.isPortrait() ? this.screenHeight : this.screenWidth;

    return (
      screenMeasurement === IPHONE_X_HEIGHT ||
      screenMeasurement === IPHONE_XS_MAX_HEIGHT ||
      screenMeasurement === IPHONE_12_HEIGHT ||
      screenMeasurement === IPHONE_12_MAX_HEIGHT ||
      screenMeasurement === IPHONE_14_MAX_HEIGHT
    );
  }

  /**
   * Get the vertical offset needed for iOS tournament highlights to account for
   * coordinate system differences and safe area insets.
   * 
   * iOS uses a different coordinate system than Android, and notched iPhones have
   * additional safe area insets that need to be accounted for.
   * 
   * This method now uses dynamic calculations based on device height to provide
   * better positioning across all device sizes.
   * 
   * @returns The Y-axis offset to apply (negative value moves content up)
   */
  // Sizes.ts
// Calibrated for iPhone 15 Pro Max ≈ -60 px
  // Sizes.ts
getIOSTournamentHighlightOffset(): number {
  const h = this.screenHeight; // dp/pt from Dimensions.get('window').height

  // ── ANDROID: small upward nudge based on height bucket ───────────────
  // Your AVD (1080 × 2400 px @ 420 dpi) → ~914 dp height ⇒ x-large bucket.
  // Returns −12 there, which centers the highlight better on tall Androids.
  if (Platform.OS === 'android') {
    const h = this.screenHeight;
    // device-height buckets roughly in dp:
    if (h >= 1000) return -2;    // ultra-tall phablets (Pixel 8 Pro, S24 Ultra)
    if (h >= 900)  return 0;     // 1080×2400 class (your current AVD)
    if (h >= 820)  return +2;    // mid-tall (Pixel 7, OnePlus 9)
    if (h >= 740)  return +4;    // typical 1080×2340 / 2220 mid-range
    if (h >= 660)  return +6;    // small phones (~6.1")
    return +8;                   // legacy small screens
  }

  // ── iOS: calibrate around iPhone 15 Pro Max target of ≈ −60 ──────────
  // Base scales with height so smaller phones get less shift.
  const base = -Math.round(h * (this.isTablet() ? 0.042 : 0.0535));

  // Notched devices get a modest extra nudge; enough to hit ~−60 on 15 Pro Max.
  const notchBonus = this.isNotchedIphone()
    ? this.dynamic({ sm: -4, md: -6, lg: -8, xlg: -10, tablet: -6 })
    : 0;

  // Keep within a safe window across the lineup.
  const total = Math.max(-68, Math.min(-34, base + notchBonus));
  return total;
}


  isAndroidSmallPortraitTablet(): boolean {
    return this._isAndroidSmallTablet() && !this.isAndroidSmallLandscapeTablet();
  }

  isAndroidSmallLandscapeTablet(): boolean {
    const ratio = this.getUIWidth() / this.getUIHeight();
    const minThreshold = Math.min(this.screenHeight, this.screenWidth);
    return (
      Platform.OS === 'android' &&
      this.isLandscape() &&
      this.isTablet() &&
      ratio >= 1.5 &&
      minThreshold < SMALL_ANDROID_TABLET_MIN_DIM_THRESHOLD
    );
  }

  _isAndroidSmallTablet(): boolean {
    const ratio = this.isPortrait()
      ? this.getUIHeight() / this.getUIWidth()
      : this.getUIWidth() / this.getUIHeight();
    return (
      Platform.OS == 'android' &&
      ratio >= 1.5 /* eslint-disable-line no-magic-numbers */ &&
      this.isTablet() &&
      Math.min(this.screenHeight, this.screenWidth) < SMALL_TABLET_MIN_DIM_THRESHOLD
    );
  }

  meetsSizeRequirements({
    minHeight = 0,
    minWidth = 0,
  }: {minHeight?: number; minWidth?: number} = {}): boolean {
    const meetsHeightRequirement = this.screenHeight >= minHeight;
    const meetsWidthRequirement = this.screenWidth >= minWidth;
    return meetsHeightRequirement && meetsWidthRequirement;
  }
}

const Sizes = new SizesClass();

export {Sizes as default};
